import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import FilterIcon from '~/icons/components/FilterIcon';
import GridViewSelectorIcon from '~/icons/components/GridViewSelectorIcon';
import FormattedMessage from '~/techstyle-shared/react-intl/src/FormattedMessage/FormattedMessage';

import { useGridFacets } from '../context/GridDataContexts';

const Container = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: fixed;
  bottom: 16px;
  right: 50vw;
  transform: translateX(50%)
    translateY(${({ $visible }) => ($visible ? '0' : 'calc(100% + 16px)')});
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  padding: 6px;
  background-color: #fff;
  z-index: 1000;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(5px);
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
`;

const GridViewSelectorButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 12px;
  cursor: pointer;
`;

const StyledSeparator = styled.div`
  width: 1px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.gridV2FilterSeparator};
`;

const StyledFilterCount = styled.div`
  font-weight: 500;
`;

const FilterAndCount = styled.button`
  display: flex;
  height: 40px;
  padding: 0 8px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
`;

const FilterIconAndText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.gridV2FloatingControlsText};
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: ${16 / 14};
`;

const CountDivider = styled.div`
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.gridV2FloatingControlsText};
  flex-shrink: 0;
  margin-top: 2px;
`;

const FloatingControls = ({ openFiltersPanel = () => {} }) => {
  // TODO: https://techstyle-prod.atlassian.net/browse/FLCE-3055
  const isGridViewSelectorEnabled = false;

  const { selectedUrlFilters } = useGridFacets();
  const filterCount = Object.entries(selectedUrlFilters).reduce(
    (count, [, value]) => count + (Array.isArray(value) ? value.length : 1),
    0
  );

  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef(
    typeof window !== 'undefined' ? window.scrollY : 0
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Hide on scroll down, show on scroll up. Skip tiny jitter (< 4px) so the
    // button doesn't flicker during momentum/rubber-band scrolls.
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;
      if (Math.abs(delta) < 4) {
        return;
      }
      setIsVisible(delta < 0 || currentScrollY <= 0);
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Portal to document.body so the fixed-position container is not trapped
  // inside the page's `<Body>` stacking context (which uses `isolation: isolate`),
  // otherwise footer content can render on top despite z-index: 1000.
  return (
    <>
      {createPortal(
        <Container $visible={isVisible}>
          {isGridViewSelectorEnabled && (
            <>
              <GridViewSelectorButton type="button">
                <GridViewSelectorIcon />
              </GridViewSelectorButton>
              <StyledSeparator />
            </>
          )}
          <FilterAndCount type="button" onClick={openFiltersPanel}>
            <FilterIconAndText>
              <FilterIcon />
              <FormattedMessage
                id="grid_static.filter_sort"
                defaultMessage="Filter & Sort"
              />
            </FilterIconAndText>

            {filterCount > 0 && (
              <>
                <CountDivider />
                <StyledFilterCount>{filterCount}</StyledFilterCount>
              </>
            )}
          </FilterAndCount>
        </Container>,
        document.body
      )}
    </>
  );
};

FloatingControls.propTypes = {
  openFiltersPanel: PropTypes.func,
};

// Memoize so products-driven re-renders in the parent grid view don't cascade
// into this component. FloatingControls only subscribes to FacetsContext.
export default React.memo(FloatingControls);
